import { useEffect, useMemo, useState } from 'react';
import type { OrganizationMemberPreview, OrganizationNode } from './model';
import type { OrganizationPort } from './port';
import { fixtureOrganizationMemberPreview } from './fixtures';

/** 기준일별 주 소속 미리보기만 최대 4건씩 조회하며, 실패는 조직 조회 자체를 막지 않습니다. */
export function useOrganizationMemberPreviews(port: OrganizationPort, organizations: readonly OrganizationNode[], asOf: string, enabled: boolean, fixtureMode: boolean) {
  const cache = useMemo(() => new Map<string, Promise<OrganizationMemberPreview | undefined>>(), [port, fixtureMode]);
  const enqueue = useMemo(() => {
    let running = 0;
    const waiting: Array<() => void> = [];
    const drain = () => { while (running < 4 && waiting.length) { running++; waiting.shift()!(); } };
    return (request: () => Promise<OrganizationMemberPreview>) => new Promise<OrganizationMemberPreview>((resolve, reject) => {
      waiting.push(() => { void request().then(resolve, reject).finally(() => { running--; drain(); }); });
      drain();
    });
  }, [port, fixtureMode]);
  const [result, setResult] = useState<{ cache: typeof cache; date: string; previews: Map<string, OrganizationMemberPreview> }>({ cache, date: '', previews: new Map() });
  const idsKey = JSON.stringify(organizations.map(node => node.id));
  useEffect(() => {
    if (!enabled || (!fixtureMode && !port.listMembers)) return;
    let active = true;
    const ids = JSON.parse(idsKey) as string[];
    let index = 0;
    setResult(current => current.cache === cache && current.date === asOf ? current : { cache, date: asOf, previews: new Map() });
    const consume = async () => {
      while (active && index < ids.length) {
        const id = ids[index++];
        const key = JSON.stringify([asOf, id]);
        if (!cache.has(key)) {
          cache.set(key, enqueue(() => fixtureMode ? Promise.resolve(fixtureOrganizationMemberPreview(id)) : port.listMembers!(id, asOf))
            .catch(() => { cache.delete(key); return undefined; }));
        }
        const value = await cache.get(key);
        if (active && value) setResult(current => ({ cache, date: asOf, previews: new Map(current.cache === cache && current.date === asOf ? current.previews : []).set(id, value) }));
      }
    };
    void Promise.all(Array.from({ length: Math.min(4, ids.length) }, consume));
    return () => { active = false; };
  }, [asOf, cache, enabled, enqueue, fixtureMode, idsKey, port]);
  return useMemo(() => result.cache === cache && result.date === asOf ? result.previews : new Map<string, OrganizationMemberPreview>(), [asOf, cache, result]);
}
